import type { ImageCategoryName } from '@/types/domains/ProductType';
import { PRODUCT_CONSTANTS } from '@/consts/ProductConst';

/**
 * getProductImageUrl
 * @description コードから商品画像のURLを返す.
 *
 * @param itemCode 商品コード
 * @param imageCategoryName 商品画像カテゴリ名
 * @param specifiedFileName 関数呼び出し側が指定するファイル名（省略可能）
 * @param includeTimestamp タイムスタンプを含むか否か（デフォルトは含む）
 *
 * @return string
 */
export const getProductImageUrl = (
  itemCode: string,
  imageCategoryName: ImageCategoryName,
  specifiedFileName: string = '',
  includeTimestamp: boolean = true
): string => {
  const { isLargeScreen } = useDeviceCheck();

  const baseUrl: string = `/image/product/${itemCode}/`;

  const imageFileSuffix: string = isLargeScreen.value ? 'pc' : 'sp';
  // 空文字の場合はデフォルトのファイル名を使用
  const imageUrl: string = specifiedFileName
    ? specifiedFileName
    : `${imageCategoryName}_${imageFileSuffix}.${PRODUCT_CONSTANTS.IMAGE_PERMIT_EXTENSION}`;

  // msの下5桁を削除（バージョニング対応かつhydration error対策）
  const removeLastFiveDigits = (milliseconds: number): number => {
    return milliseconds - (milliseconds % 100000);
  };
  const timestampSuffix: string = includeTimestamp
    ? `?${removeLastFiveDigits(new Date().getTime())}`
    : '';

  return `${baseUrl}${imageUrl}${timestampSuffix}`;
};

/**
 * 画像パスを適切な形式に変換する関数。
 *
 * @param {string} originalImagePath - 元の画像パス。
 * @param {boolean} isLargeScreen - 大画面かどうかを示すフラグ。
 * @returns {string} - 適切な形式に変換された画像パス。
 *
 * この関数は、与えられた画像パスが '_sp.' または '_pc.' を含むかどうかをチェックし、
 * 画面サイズに応じて適切な画像パスに変換します。
 *
 * - 画像パスが '_sp.' や '_pc.' を含まない場合、そのままのパスを返します。
 * - 大画面の場合:
 *   - 画像パスが '_pc.' を含む場合、そのままのパスを返します。
 *   - 画像パスが '_sp.' を含む場合、 '_sp.' を '_pc.' に置換して返します。
 * - 小画面の場合:
 *   - 画像パスが '_sp.' を含む場合、そのままのパスを返します。
 *   - 画像パスが '_pc.' を含む場合、 '_pc.' を '_sp.' に置換して返します。
 */
export const convertSuitableImagePath = (
  originalImagePath: string,
  isLargeScreen: boolean
): string => {
  const pcSuffix: string = '_pc.';
  const spSuffix: string = '_sp.';
  const [currentSuffix, targetSuffix] = isLargeScreen ? [spSuffix, pcSuffix] : [pcSuffix, spSuffix];

  return originalImagePath.includes(currentSuffix)
    ? originalImagePath.replace(currentSuffix, targetSuffix)
    : originalImagePath;
};
